import { feedbackKindLabels, feedbackStatusLabels, type FeedbackStatus } from '@nexo/types';
import { Badge, Button, type BadgeTone } from '@nexo/ui';
import { useQuery } from '@tanstack/react-query';
import { MessageSquareWarning } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../../lib/api';
import { formatRelative } from '../../lib/format';
import { FeedbackDialog, myFeedbackKey } from './FeedbackDialog';

export const feedbackStatusTone: Record<FeedbackStatus, BadgeTone> = {
  new: 'neutral',
  in_progress: 'accent',
  done: 'success',
  dismissed: 'neutral',
};

/** Profile section: the person's reports and what the Nexo team answered. */
export function MyFeedback() {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const reports = useQuery({ queryKey: myFeedbackKey, queryFn: api.feedback.mine });
  const list = reports.data ?? [];

  return (
    <section className="mt-4 rounded-2xl border border-border bg-surface p-6 shadow-panel">
      <div className="mb-3 flex items-center gap-3">
        <h2 className="text-sm font-semibold">{t('Errores y mejoras')}</h2>
        <Button
          size="sm"
          variant="secondary"
          className="ml-auto"
          icon={<MessageSquareWarning className="size-4" />}
          onClick={() => setOpen(true)}
        >
          {t('Reportar')}
        </Button>
      </div>
      {list.length === 0 ? (
        <p className="text-sm text-muted">
          {t('¿Algo no funciona o echas algo en falta? Cuéntanoslo y aquí verás en qué queda.')}
        </p>
      ) : (
        <ul className="flex flex-col divide-y divide-border">
          {list.map((report) => (
            <li key={report.id} className="py-2.5">
              <div className="flex flex-wrap items-center gap-2">
                <span className="min-w-0 flex-1 truncate text-sm font-medium">{report.title}</span>
                <span className="text-xs text-subtle">{t(feedbackKindLabels[report.kind])}</span>
                <Badge tone={feedbackStatusTone[report.status]}>
                  {t(feedbackStatusLabels[report.status])}
                </Badge>
              </div>
              <p className="text-xs text-subtle">
                {t('Enviado {{when}}', { when: formatRelative(report.createdAt) })}
              </p>
              {report.reply && (
                <p className="mt-1.5 rounded-lg bg-surface-2 px-3 py-2 text-[13px] whitespace-pre-line text-muted">
                  <span className="font-medium text-text">{t('Equipo de Nexo:')} </span>
                  {report.reply}
                </p>
              )}
            </li>
          ))}
        </ul>
      )}
      <FeedbackDialog open={open} onClose={() => setOpen(false)} />
    </section>
  );
}
